/**
 * Каталоги сообщений: `messages/<язык>/<пространство>.json`, формат —
 * плоский `{ id: "ICU-строка" }` (ADR-0019).
 *
 * * `ru` — источник; файлы генерирует `npm run i18n:extract` из
 *   `defineMessages` в коде, руками их не правят;
 * * `uz-Latn` — перевод (из системы переводов / руками), ключи — подмножество `ru`;
 * * `uz-Cyrl` — выводится из `uz-Latn` транслитерацией (`npm run i18n:cyrl`)
 *   плюс вычитанные правки `overrides/uz-Cyrl.json`.
 *
 * Пространство имён — первый сегмент id (`shell.nav.farms` → `shell`).
 * Ядро (`CORE_NAMESPACES`) нужно оболочке и грузится до первого кадра;
 * пространства экранов грузятся лениво, когда экран их попросит
 * (`useNamespace`), — каждый язык и каждое пространство отдельным чанком.
 * Русское ядро вшито в основной пакет: язык-источник — откат для любого
 * пропуска, и он обязан быть под рукой без сети.
 */
import { SOURCE_LOCALE, type Locale } from './locales';
import type { MessageId } from './messageIds.generated';

export type Catalog = Record<string, string>;

/**
 * Каталог, слитый с источником: в нём есть каждый id (`ru` полон по
 * построению — его генерирует извлечение). Тип для `IntlProvider`, который
 * требует запись по всем id.
 */
export type CompleteCatalog = Record<MessageId, string>;

export const CORE_NAMESPACES = ['common', 'shell', 'auth', 'errors', 'units'] as const;

const SOURCE_CORE = import.meta.glob<Catalog>(
  [
    './messages/ru/common.json',
    './messages/ru/shell.json',
    './messages/ru/auth.json',
    './messages/ru/errors.json',
    './messages/ru/units.json',
  ],
  { eager: true, import: 'default' },
);

const LAZY = import.meta.glob<Catalog>(
  [
    './messages/*/*.json',
    '!./messages/ru/common.json',
    '!./messages/ru/shell.json',
    '!./messages/ru/auth.json',
    '!./messages/ru/errors.json',
    '!./messages/ru/units.json',
  ],
  { import: 'default' },
);

const path = (locale: Locale, namespace: string) => `./messages/${locale}/${namespace}.json`;

/**
 * Пространства, которые грузятся для языка целиком при старте и при смене
 * языка. Источнику — только ядро: остальные его строки — `defaultMessage`
 * описаний в коде, грузить их второй раз незачем. Переводу — ВСЕ его
 * пространства одним заходом (журнал №1101): строки экранов строятся и вне
 * React (модели, словари, форматтеры), и «забытый `useNamespace`» показывал
 * бы узбекоязычному человеку русский экран без единой ошибки.
 */
export function namespacesFor(locale: Locale): string[] {
  if (locale === SOURCE_LOCALE) return [...CORE_NAMESPACES];
  const prefix = `./messages/${locale}/`;
  const own = Object.keys(LAZY)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length).replace(/\.json$/, ''));
  return [...new Set<string>([...CORE_NAMESPACES, ...own])];
}

/** Пространство имён id: первый сегмент. */
export function namespaceOf(id: string): string {
  return id.split('.', 1)[0] ?? id;
}

/** Ядро языка-источника — синхронно (для первого кадра и рендера без провайдера). */
export function sourceCore(): Catalog {
  return Object.assign({}, ...CORE_NAMESPACES.map((ns) => SOURCE_CORE[path(SOURCE_LOCALE, ns)] ?? {}));
}

const loaded = new Map<string, Catalog>();

async function loadOne(locale: Locale, namespace: string): Promise<Catalog> {
  const key = path(locale, namespace);
  const eager = SOURCE_CORE[key];
  if (eager) return eager;
  const cached = loaded.get(key);
  if (cached) return cached;
  const loader = LAZY[key];
  // Файла нет — у языка нет перевода этого пространства: откат к источнику,
  // а не ошибка экрана.
  const catalog = loader ? await loader() : {};
  loaded.set(key, catalog);
  return catalog;
}

/**
 * Сообщения языка для пространств имён: источник, поверх — перевод.
 * Пустая строка перевода — пропуск (так их выгружают системы переводов), а
 * не «показать пусто».
 */
export async function loadMessages(locale: Locale, namespaces: readonly string[]): Promise<Catalog> {
  const parts = await Promise.all(
    // Источник подкладывается только под ядро: строки экранов при пропуске
    // перевода откатываются к `defaultMessage` описания — второй загрузки
    // русского каталога экрана это не требует.
    namespaces.flatMap((ns) => [
      locale === SOURCE_LOCALE || (CORE_NAMESPACES as readonly string[]).includes(ns) ? loadOne(SOURCE_LOCALE, ns) : null,
      locale === SOURCE_LOCALE ? null : loadOne(locale, ns),
    ]),
  );
  const merged: Catalog = {};
  for (const part of parts) {
    if (!part) continue;
    for (const [id, text] of Object.entries(part)) if (text) merged[id] = text;
  }
  return merged;
}
